/**
 * Hero Malbec — scroll parallax.
 *
 * grouped: texto fixo no viewport até o fundo da hero (data-cta-gap), depois sobe.
 * Mobile: gmStableVh + gm:vv-chrome (reancora sticky quando URL bar some).
 * Mobile: scroll linear (sem low-pass) pra evitar “pulos”.
 */
if (!customElements.get('gm-hero-text-parallax')) {
  const SMOOTH_TAU_DESKTOP = 0.12;
  const MAX_DT = 0.05;

  class GmHeroTextParallax extends HTMLElement {
    connectedCallback() {
      this._section =
        this.closest('.gm-hero-scroll') || this.closest('.gm-hero');
      this._media = this._section?.querySelector('.gm-hero__media');
      this._stack = this.querySelector('.gm-hero__slide-copy-stack');
      this._title = this.querySelector('.gm-hero__slide-title');
      this._titleBlock = this.querySelector('.gm-hero__title-with-lock');
      this._titleMotion = this._titleBlock || this._title;
      this._cta = this.querySelector('.gm-hero__slide-cta');
      this._mode = this.dataset.copyMode === 'split' ? 'split' : 'grouped';
      this._reduced = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      const hasCopy = this._title && this._cta;

      if (!this._section || !this._media || !hasCopy || this._reduced) {
        return;
      }

      this.classList.toggle('gm-hero__slide-copy--grouped', this._mode === 'grouped');
      this.classList.toggle('gm-hero__slide-copy--split', this._mode === 'split');

      this._titleFactor = parseFloat(this.dataset.titleFactor || '1');
      this._ctaGap = parseFloat(this.dataset.ctaGap || '24');
      this._zoom = parseFloat(this.dataset.zoom || '0.1');
      this._titleZoom = parseFloat(this.dataset.titleZoom || '0');
      this._copyAnchor = 0;
      this._stickyTop = null;
      this._raf = null;
      this._smoothY = window.scrollY;
      this._lastT = 0;
      this._linearMobile = window.matchMedia('(max-width: 749px)').matches;

      this._layoutVh = () =>
        window.gmStableVh?.px() ?? Math.round(window.innerHeight);
      this._vhGen = window.gmStableVh?.gen() ?? 0;

      this._lockHeroHeight = () => {
        if (!this._section) return;
        this._section.style.setProperty(
          '--gm-hero-height',
          `${this._layoutVh()}px`
        );
      };

      this._loop = (t) => {
        const dt = this._lastT ? Math.min((t - this._lastT) / 1000, MAX_DT) : 1 / 60;
        this._lastT = t;

        const targetY = window.scrollY;
        if (this._linearMobile) {
          this._smoothY = targetY;
        } else {
          const alpha = 1 - Math.exp(-dt / SMOOTH_TAU_DESKTOP);
          this._smoothY += (targetY - this._smoothY) * alpha;
          if (Math.abs(targetY - this._smoothY) < 0.5) this._smoothY = targetY;
        }

        this._update();

        if (this._smoothY !== targetY) {
          this._raf = requestAnimationFrame(this._loop);
        } else {
          this._raf = null;
        }
      };

      this._wake = () => {
        if (this._raf == null) {
          this._lastT = 0;
          this._raf = requestAnimationFrame(this._loop);
        }
      };

      this._onResize = () => {
        window.requestAnimationFrame(() => {
          const g = window.gmStableVh?.gen();
          if (g != null && g === this._vhGen) {
            this._wake();
            return;
          }
          if (g != null) this._vhGen = g;
          this._stickyTop = null;
          this._lockHeroHeight();
          this._measureAnchor();
          this._wake();
        });
      };

      /* URL bar: reancora sticky na posição visual atual (não parece scroll reverso). */
      this._onVvChrome = () => {
        if (this._mode !== 'grouped') {
          this._wake();
          return;
        }
        if (this._smoothY < 2 || this._stickyTop == null) {
          this._wake();
          return;
        }
        /* Onde o bloco está AGORA na tela vira o novo alvo sticky. */
        const visualTop = this._titleMotion.getBoundingClientRect().top;
        const ctaTop = this._cta.getBoundingClientRect().top;
        /* sticky é âncora do CTA (copyAnchor); alinha pelo CTA visível. */
        this._stickyTop = ctaTop;
        void visualTop;
        this._wake();
      };

      this._lockHeroHeight();
      this._measureAnchor();
      this._update();
      document.fonts?.ready.then(() => {
        if (this._smoothY < 2) this._stickyTop = null;
        this._measureAnchor();
        this._wake();
      });
      window.addEventListener('scroll', this._wake, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
      window.addEventListener('gm:vv-chrome', this._onVvChrome);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._wake);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('gm:vv-chrome', this._onVvChrome);
      if (this._raf) cancelAnimationFrame(this._raf);
      this._reset();
    }

    _reset() {
      if (this._media) this._media.style.transform = '';
      if (this._titleMotion) this._titleMotion.style.transform = '';
      if (this._title) {
        this._title.style.transform = '';
        this._title.style.transformOrigin = '';
        this._title.style.opacity = '';
      }
      if (this._stack) {
        this._stack.style.transform = '';
        this._stack.style.opacity = '';
        this._stack.classList.remove('is-pinned');
      }
      if (this._cta) {
        this._cta.style.transform = '';
        this._cta.classList.remove('is-pinned');
      }
      this._stickyTop = null;
    }

    /* Translate no wrapper (lock + título). Scale só no h2 — a fechadura
       não herda zoom (spec 0025: max = tamanho do 1º paint). */
    _setTitleMotion(y, titleScale) {
      const yPx = y.toFixed(2);
      if (this._title && this._title !== this._titleMotion) {
        this._titleMotion.style.transform = `translate3d(0, ${yPx}px, 0)`;
        this._title.style.transformOrigin = 'top center';
        this._title.style.transform = `scale(${titleScale})`;
        return;
      }
      this._titleMotion.style.transform =
        `translate3d(0, ${yPx}px, 0) scale(${titleScale})`;
    }

    _clamp(v, min, max) {
      return v < min ? min : v > max ? max : v;
    }

    _measureAnchor() {
      if (!this._cta) return;

      const prevCta = this._cta.style.transform;
      const prevWrap = this._titleMotion ? this._titleMotion.style.transform : '';
      const prevTitle = this._title ? this._title.style.transform : '';
      this._cta.style.transform = '';
      if (this._titleMotion) this._titleMotion.style.transform = '';
      if (this._title && this._title !== this._titleMotion) {
        this._title.style.transform = '';
      }

      const top = this._cta.getBoundingClientRect().top;
      const sectionTop = this._section.getBoundingClientRect().top;
      this._copyAnchor = top - sectionTop;

      this._cta.style.transform = prevCta;
      if (this._titleMotion) this._titleMotion.style.transform = prevWrap;
      if (this._title && this._title !== this._titleMotion) {
        this._title.style.transform = prevTitle;
      }
    }

    _updateMedia(progress) {
      if (!this._media) return;
      if (progress <= 0.001) {
        this._media.style.transform = '';
        return;
      }
      const scale = (1 + progress * this._zoom).toFixed(4);
      this._media.style.transform = `scale(${scale})`;
    }

    _updateSplit(scrolled, mediaTop, mediaBottom, progress) {
      const titleY = -scrolled * this._titleFactor - mediaTop;
      const titleScale = (1 + progress * this._titleZoom).toFixed(4);
      this._setTitleMotion(titleY, titleScale);
      this._title.style.opacity = (
        1 - this._clamp((progress - 0.5) / 0.4, 0, 1)
      ).toFixed(3);

      const ctaH = this._cta.offsetHeight;
      const pinLine = mediaBottom - this._ctaGap - ctaH;
      const naturalTop = mediaTop + this._copyAnchor;
      const desired = Math.min(naturalTop, pinLine);
      const ctaY = desired - naturalTop;
      this._cta.style.transform = `translate3d(0, ${ctaY.toFixed(2)}px, 0)`;
      this._cta.classList.toggle('is-pinned', pinLine < naturalTop - 0.5);
    }

    _updateGrouped(mediaTop, mediaBottom, progress) {
      const mediaH = mediaBottom - mediaTop;
      if (mediaH < 80) {
        this._titleMotion.style.transform = '';
        if (this._title && this._title !== this._titleMotion) {
          this._title.style.transform = '';
        }
        this._cta.style.transform = '';
        return;
      }

      const ctaH = this._cta.offsetHeight;
      const pinLine = mediaBottom - this._ctaGap - ctaH;
      const naturalTop = mediaTop + this._copyAnchor;

      if (this._smoothY < 2) {
        this._stickyTop = naturalTop;
      } else if (this._stickyTop == null) {
        this._stickyTop = naturalTop;
      }

      const desired = Math.min(this._stickyTop, pinLine);
      const copyY = desired - naturalTop;
      const titleScale = (1 + progress * this._titleZoom).toFixed(4);

      this._setTitleMotion(copyY, titleScale);
      this._title.style.opacity = '1';
      this._cta.style.transform = `translate3d(0, ${copyY.toFixed(2)}px, 0)`;
      this._cta.classList.toggle('is-pinned', Math.abs(copyY) > 0.5);

      if (this._stack) {
        this._stack.style.transform = '';
        this._stack.style.opacity = '';
        this._stack.classList.remove('is-pinned');
      }
    }

    _update() {
      const sectionRect = this._section.getBoundingClientRect();
      const mediaRect = this._media.getBoundingClientRect();

      const adjust = window.scrollY - this._smoothY;
      const sectionTop = sectionRect.top + adjust;
      const mediaTop = mediaRect.top + adjust;
      const mediaBottom = mediaRect.bottom + adjust;

      const scrolled = Math.max(0, -sectionTop);
      const progress = this._clamp(scrolled / this._layoutVh(), 0, 1);

      this._updateMedia(progress);

      if (this._mode === 'grouped') {
        this._updateGrouped(mediaTop, mediaBottom, progress);
      } else {
        this._updateSplit(scrolled, mediaTop, mediaBottom, progress);
      }
    }
  }

  customElements.define('gm-hero-text-parallax', GmHeroTextParallax);
}
